import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

interface SegmentedProps<T extends string | number> {
  label: string
  value: T
  /** `count` shows small and quiet beside the label, like "All 6". */
  options: { value: T; label: ReactNode; count?: number }[]
  onChange: (value: T) => void
  /** `lg` for a choice the page is built around, such as the billing period. */
  size?: 'md' | 'lg'
  className?: string
}

/** A single-choice switcher for views and filters (radio group semantics). */
export function Segmented<T extends string | number>({
  label,
  value,
  options,
  onChange,
  size = 'md',
  className,
}: SegmentedProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'inline-flex max-w-full flex-wrap rounded-[1.25rem] border border-border bg-surface-sunken p-[3px]',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-accent',
              size === 'lg'
                ? 'h-9 px-4 text-[13px] sm:h-10 sm:px-6 sm:text-sm'
                : 'h-[30px] px-3.5 text-[13px]',
              selected
                ? 'bg-surface-raised font-semibold text-ink shadow-[0_1px_2px_rgb(0_0_0/0.1)]'
                : 'font-medium text-ink-muted hover:text-ink',
            )}
          >
            {option.label}
            {option.count !== undefined && ' '}
            {option.count !== undefined && (
              <span className="text-[11.5px] font-medium text-ink-subtle tabular-nums">{option.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
